import { useEffect, useMemo, useState } from 'react'
import { Button, Combobox, Dialog, Field, LoadingState, controlShellVariants, Logo } from '@highlight/ui'
import { Lock } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useToast } from '@renderer/overlay/Toaster/use-toast'
import {
  RECONNECT_SLACK_MESSAGE,
  filterSlackDestinations,
  isSameSlackDestination,
  isSlackDestination,
  slackDestinationLabel,
  toSlackDestinations,
  type SlackDestination,
  type SlackDestinationGroup,
  type SlackShareResult,
} from './slack-share'
import { useShareToSlack, type ShareToSlackTarget } from './use-share-to-slack'
import { useSlackDirectory } from './use-slack-directory'

export { useCanShareToSlack } from './use-can-share-to-slack'
export type { ShareToSlackTarget } from './use-share-to-slack'

const MAX_MESSAGE_LENGTH = 2000

interface ShareToSlackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  target: ShareToSlackTarget
  title: string
}

/**
 * Posts a meeting note or chat to a Slack channel or DM. The gateway builds the card; this picks
 * where it goes and the note above it, publishing the share first if it isn't public yet.
 */
export function ShareToSlackDialog({ open, onOpenChange, target, title }: ShareToSlackDialogProps) {
  useKeepHotkeysBehindModal(open)
  // Owned here rather than by the form so a share in flight still lands its toast, and the dialog
  // can refuse to close until it does.
  const share = useShareToSlack()

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true)
      return
    }
    if (share.isPending) {
      return
    }
    share.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content className="w-[440px]">
        {/* Inside the popup so the form starts fresh each time the dialog opens. */}
        <ShareToSlackForm share={share} target={target} title={title} onClose={() => handleOpenChange(false)} />
      </Dialog.Content>
    </Dialog>
  )
}

/**
 * The sidepanel's hotkeys listen on `window`, so Escape would also navigate back (unmounting this
 * dialog's view) and Tab would cycle the cells behind it. Stopping keydowns at `document` keeps them
 * from the window while Base UI's own document listeners still run.
 */
function useKeepHotkeysBehindModal(open: boolean) {
  useEffect(() => {
    if (!open) {
      return
    }
    const stop = (event: KeyboardEvent) => event.stopPropagation()
    document.addEventListener('keydown', stop)
    return () => document.removeEventListener('keydown', stop)
  }, [open])
}

interface ShareToSlackFormProps {
  share: ReturnType<typeof useShareToSlack>
  target: ShareToSlackTarget
  title: string
  onClose: () => void
}

function ShareToSlackForm({ share, target, title, onClose }: ShareToSlackFormProps) {
  const [destination, setDestination] = useState<SlackDestination | null>(null)
  const [message, setMessage] = useState('')
  const showSharedToast = useSharedToast()

  const handleShare = () => {
    if (!destination) {
      return
    }
    share.mutate(
      { target, destination, message },
      {
        onSuccess: (result) => {
          showSharedToast(destination, result)
          onClose()
        },
      },
    )
  }

  return (
    <>
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2">
          <Logo name="slack" size={18} />
          Share to Slack
        </Dialog.Title>
        <Dialog.Description className="text-foreground-secondary truncate text-sm">{title}</Dialog.Description>
      </Dialog.Header>
      <Dialog.Body>
        <DestinationField value={destination} onChange={setDestination} disabled={share.isPending} />
        <Field>
          <Field.Label>
            Message <span className="text-foreground-secondary font-normal">(optional)</span>
          </Field.Label>
          <Field.Control
            render={<textarea rows={3} maxLength={MAX_MESSAGE_LENGTH} />}
            value={message}
            onValueChange={setMessage}
            disabled={share.isPending}
            placeholder="Add a note"
            className={twMerge(controlShellVariants(), 'h-auto resize-none py-2')}
          />
        </Field>
        {share.error && (
          <p role="alert" className="text-danger my-0 text-sm">
            {share.error.message}
          </p>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Close variant="outline" disabled={share.isPending}>
          Cancel
        </Dialog.Close>
        <Button variant="primary" onClick={handleShare} disabled={!destination} loading={share.isPending}>
          Share
        </Button>
      </Dialog.Footer>
    </>
  )
}

function useSharedToast() {
  const toast = useToast()

  return (destination: SlackDestination, result: SlackShareResult) => {
    const permalink = result.permalink
    toast.add({
      analytics: { type: 'shared to slack', destinationType: destination.type },
      icon: <Logo name="slack" size={16} />,
      title:
        destination.type === 'channel'
          ? `Shared to ${slackDestinationLabel(destination)}`
          : `Shared with ${slackDestinationLabel(destination)}`,
      actions: permalink
        ? [{ variant: 'secondary', children: 'Open in Slack', onClick: () => window.open(permalink, '_blank') }]
        : undefined,
    })
  }
}

interface DestinationFieldProps {
  value: SlackDestination | null
  onChange: (destination: SlackDestination | null) => void
  disabled: boolean
}

function DestinationField({ value, onChange, disabled }: DestinationFieldProps) {
  const directory = useSlackDirectory(true)
  const destinations = useMemo(() => (directory.data ? toSlackDestinations(directory.data) : []), [directory.data])

  return (
    <Field>
      <Field.Label>Send to</Field.Label>
      {directory.isPending ? (
        <LoadingState className="text-foreground-secondary h-7">
          <LoadingState.Spinner />
          Loading your Slack workspace…
        </LoadingState>
      ) : directory.isError ? (
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-foreground-secondary">Could not load your Slack channels and people.</span>
          <Button variant="outline" size="sm" onClick={() => void directory.refetch()} loading={directory.isFetching}>
            Retry
          </Button>
        </div>
      ) : directory.data === null ? (
        <p className="text-foreground-secondary my-0 text-sm">{RECONNECT_SLACK_MESSAGE}</p>
      ) : destinations.length === 0 ? (
        <p className="text-foreground-secondary my-0 text-sm">No channels or people found in your Slack workspace.</p>
      ) : (
        <DestinationCombobox destinations={destinations} value={value} onChange={onChange} disabled={disabled} />
      )}
    </Field>
  )
}

function DestinationCombobox({
  destinations,
  value,
  onChange,
  disabled,
}: DestinationFieldProps & { destinations: Array<SlackDestination> }) {
  const [inputValue, setInputValue] = useState('')
  // Picking an item fills the input with its label; searching on that would hide every other option.
  const query = value && inputValue === slackDestinationLabel(value) ? '' : inputValue
  const groups = useMemo(() => filterSlackDestinations(destinations, query), [destinations, query])

  return (
    <Combobox
      className="w-full"
      items={groups}
      filteredItems={groups}
      value={value}
      onValueChange={(next) => onChange(isSlackDestination(next) ? next : null)}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      itemToStringLabel={(item) => (isSlackDestination(item) ? slackDestinationLabel(item) : '')}
      isItemEqualToValue={(a, b) => isSlackDestination(a) && isSlackDestination(b) && isSameSlackDestination(a, b)}
      disabled={disabled}
    >
      <Combobox.Input data-autofocus placeholder="Search channels and people" />
      <Combobox.Content className="max-h-64">
        <Combobox.Empty>No channels or people match.</Combobox.Empty>
        <Combobox.List>
          {(group: SlackDestinationGroup) => (
            <Combobox.Group key={group.label} items={group.items}>
              <Combobox.GroupLabel className="text-foreground-secondary px-3 pb-1 pt-2 text-xs font-medium">
                {group.label}
              </Combobox.GroupLabel>
              <Combobox.Collection>
                {(destination: SlackDestination) => (
                  <Combobox.Item
                    key={`${destination.type}:${destination.id}`}
                    value={destination}
                    showIndicator={false}
                  >
                    <DestinationRow destination={destination} />
                  </Combobox.Item>
                )}
              </Combobox.Collection>
            </Combobox.Group>
          )}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  )
}

function DestinationRow({ destination }: { destination: SlackDestination }) {
  if (destination.type === 'channel') {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span className="text-foreground-secondary w-5 shrink-0 text-center">#</span>
        <span className="truncate">{destination.name}</span>
        {destination.isPrivate && (
          <Lock aria-label="Private channel" className="text-foreground-secondary size-3 shrink-0" />
        )}
      </span>
    )
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      {destination.image ? (
        <img src={destination.image} alt="" className="size-5 shrink-0 rounded-full" />
      ) : (
        <span
          aria-hidden="true"
          className="bg-element-hover text-foreground-secondary flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
        >
          {destination.name.charAt(0).toUpperCase()}
        </span>
      )}
      <span className="truncate">{destination.name}</span>
      {destination.detail && <span className="text-foreground-secondary truncate">{destination.detail}</span>}
    </span>
  )
}
